/* eslint-disable lingui/no-unlocalized-strings -- Approved Australian institutional marketing copy. */
/* eslint-disable lingui/text-restrictions -- Preserve approved Australian marketing punctuation. */
import { FIRST_ROUND_INDUSTRY_PAGES } from "@reactive-resume/utils";

import { FIRST_ROUND_ORIGIN } from "@/client/utils/firstRoundAppUrl";

export const INSTITUTIONS_PATH = "/for-educational-institutes";
export const INSTITUTIONS_EMAIL = "institutions@theresume.io";
export const INSTITUTIONS_CONTACT = `mailto:${INSTITUTIONS_EMAIL}?subject=${encodeURIComponent("Educational institute pilot enquiry")}`;

export const INSTITUTIONS_META = {
  ...FIRST_ROUND_INDUSTRY_PAGES[INSTITUTIONS_PATH],
  url: `${FIRST_ROUND_ORIGIN}${INSTITUTIONS_PATH}`,
  image: `${FIRST_ROUND_ORIGIN}/brand/firstround/og.png`,
};

export type Segment = "providers" | "faculty";
export const getSegment = (search: string): Segment =>
  new URLSearchParams(search).get("for") === "faculty" ? "faculty" : "providers";

export const audiences = {
  providers: {
    label: "Providers and colleges",
    body: "For private higher education providers, pathway colleges and vocational providers, the starting point is the cohort and the opportunities students are preparing for. Shape a pilot around that transition, with a named member of your team involved.",
    points: [
      [
        "A defined student journey",
        "Agree the preparation activities that suit the course and intended roles.",
      ],
      [
        "Room to practise",
        "Give students opportunities to rehearse and revisit feedback before seeking further advice.",
      ],
      [
        "Cohort reporting",
        "Agree participation measures and the format your team needs for its review.",
      ],
      ["A manageable start", "Begin with one cohort and a clear division of responsibilities."],
    ],
  },
  faculty: {
    label: "Faculties and schools",
    body: "Start with a course or school where career preparation has a clear role. Scope practice around the discipline, involve your employability lead and agree how the pilot fits alongside existing careers support.",
    points: [
      ["Discipline-specific preparation", "Agree target roles and relevant practice questions."],
      [
        "Evidence for course discussions",
        "Define the participation and progression measures you want to review.",
      ],
      [
        "Connected coaching",
        "Decide where advisers join the student journey and what information they need.",
      ],
      [
        "A bounded pilot",
        "Agree scope, timing and your institution’s approval process before starting.",
      ],
    ],
  },
} as const;

export const studentFeatures = [
  {
    title: "Resumes",
    body: "Build and keep tailored versions for different opportunities.",
    why: "Help students draw on coursework, projects, placements and work experience when explaining their skills.",
  },
  {
    title: "Spotlight Intro",
    body: "Record a short introduction to accompany their profile.",
    why: "Give students another way to introduce themselves and describe relevant experience.",
  },
  {
    title: "Practice interviews",
    body: "Rehearse for target roles and review structured feedback.",
    why: "Use practice to identify what to improve and what to discuss with an adviser.",
  },
  {
    title: "Applications and interviews",
    body: "Search opportunities in the Apex 25 employer network, follow applications made through theResume and attend employer-invited interviews.",
    why: "Availability depends on participating employers; feedback depends on the interview workflow.",
  },
] as const;

export const pilotSteps = [
  [
    "Discuss the fit",
    "Tell us about the cohort, target opportunities and support already available.",
  ],
  [
    "Agree the scope",
    "Define activities, access, responsibilities, success measures and commercial terms.",
  ],
  [
    "Run the pilot",
    "Introduce students to the agreed experience, with named contacts on both sides.",
  ],
  [
    "Review and decide",
    "Review participation, student and adviser feedback, and progress against the agreed measures. Decide whether to continue or expand.",
  ],
] as const;

// Keep review-sensitive statements together. These describe pilot scoping, not certifications.
export const practicalities = [
  {
    title: "Sign-in",
    body: "Confirm the onboarding approach and any institutional sign-in requirements during scoping.",
  },
  {
    title: "Data location and processing",
    body: "Review storage locations and the external services involved in processing student data.",
    privacyLink: true,
  },
  {
    title: "Privacy and access",
    body: "Agree reporting access, student permissions and retention arrangements before onboarding.",
  },
  {
    title: "Accessibility",
    body: "Discuss required adjustments, recording alternatives and accessibility review requirements before the pilot.",
  },
  {
    title: "Security review",
    body: "Identify the vendor assessment and supporting information your institution requires.",
  },
  {
    title: "Feedback and its use",
    body: "Clarify what feedback students receive and how it is used within the pilot.",
  },
  {
    title: "Existing systems",
    body: "Agree how the pilot fits alongside current careers services and any integration requirements.",
  },
  {
    title: "Pricing",
    body: "Discuss pricing for your cohort and agreed pilot scope.",
    contact: true,
  },
] as const;

export const cohortStates = [
  { key: "completed", label: "Practice milestone completed", count: 96 },
  { key: "progress", label: "In progress", count: 71 },
  { key: "unrecorded", label: "No activity recorded", count: 73 },
] as const;

export function createCohortCells() {
  const cells = cohortStates.flatMap(({ key, count }) => Array<string>(count).fill(key));
  let seed = 20260908;
  for (let i = cells.length - 1; i > 0; i--) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const j = seed % (i + 1);
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }
  return cells;
}
